import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code, List, ListOrdered, StickyNote, Pencil, X, Check, Loader2, Trash2 } from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExtension from "@tiptap/extension-underline";
import { MascotConfirmDialog } from "@/components/common/MascotConfirmDialog";
import { useAuthRequired } from "@/contexts/AuthRequiredContext";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface SessionNoteInputProps {
  value: string;
  onSave: (value: string) => void;
  onDelete?: () => void;
  isSaving?: boolean;
}

export const SessionNoteInput = forwardRef<{ focus: () => void }, SessionNoteInputProps>(
  ({ value, onSave, onDelete, isSaving = false }, ref) => {
    const { t } = useTranslation();
    const { data: isAuthenticated } = useAuth();
    const { requireAuth } = useAuthRequired();
    const [isEditing, setIsEditing] = useState(false);
    const [localContent, setLocalContent] = useState(value);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const editor = useEditor({
      extensions: [StarterKit, UnderlineExtension],
      content: "",
      editable: true,
      onUpdate: ({ editor }) => {
        setLocalContent(editor.getHTML());
      },
      editorProps: {
        attributes: {
          class: "w-full overflow-auto px-3 py-2 text-sm focus:outline-none prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed prose-p:my-0",
          style: "min-height: 40px; max-height: 200px;",
        },
      },
    });

    useImperativeHandle(ref, () => ({
      focus: () => editor?.commands.focus(),
    }));

    useEffect(() => {
      if (isEditing && editor) {
        editor.commands.setContent(value || "");
        setLocalContent(value);
        editor.commands.focus();
      }
    }, [isEditing, editor]);

    useEffect(() => {
      if (!value || value === "<p></p>") {
        setLocalContent("");
        setIsEditing(false);
      }
    }, [value]);

    const requestNoteAuth = () =>
      requireAuth({
        title: t("auth.modal.required.noteTitle", "Sign in to keep your note"),
        description: t(
          "auth.modal.required.noteDescription",
          "Notes are saved to your account, so they stay with your vocabulary and your sessions.",
        ),
      });

    // Resolves `true` once signed in, so the note saves without a second press;
    // `false` when the gate is dismissed, and nothing is written.
    const handleSave = async () => {
      if (!isAuthenticated && !(await requestNoteAuth())) return;
      onSave(localContent);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setLocalContent(value);
      setIsEditing(false);
    };

    const handleStartEdit = async () => {
      if (!isAuthenticated && !(await requestNoteAuth())) return;
      setIsEditing(true);
    };

    const hasExistingContent = value && value.trim() !== "" && value !== "<p></p>";
    const hasLocalChanges = localContent !== value;
    const canSave = localContent.trim() !== "" && localContent !== "<p></p>" && hasLocalChanges;

    return (
      <>
        <MascotConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          onConfirm={() => {
            onDelete?.();
          }}
          title={t("pages.noteEditor.deleteNote", "Delete Note")}
          description="Are you sure you want to delete this note? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          destructive
        />

        {!isEditing && hasExistingContent ? (
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                  {t("pages.noteEditor.noteLabel", "Note")}
                </span>
              </div>
              <div className="flex items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleStartEdit}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                  title={t("pages.noteEditor.editNote", "Edit note")}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  title={t("pages.noteEditor.deleteNote", "Delete note")}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-2 rounded-xl bg-amber-50/60 dark:bg-amber-950/20 px-3 py-2.5 max-h-80 overflow-y-auto">
              <div
                className="prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed prose-p:my-0 text-sm text-foreground"
                dangerouslySetInnerHTML={{ __html: value }}
              />
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                  {t("pages.noteEditor.noteLabel", "Note")}
                </span>
              </div>
              <div className="flex items-center gap-0.5">
                {hasExistingContent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                    title={t("pages.noteEditor.cancel", "Cancel")}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSave}
                  disabled={!canSave || isSaving}
                  className={cn(
                    "h-6 w-6 p-0",
                    canSave ? "text-foreground hover:text-foreground" : "text-muted-foreground"
                  )}
                  title={t("pages.noteEditor.save", "Save")}
                >
                  {isSaving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>
            <div className="rounded-2xl border border-border/60 bg-muted/10 focus-within:bg-background focus-within:ring-1 focus-within:ring-primary/20 focus-within:border-primary/30 transition-all duration-200 overflow-hidden">
              <div className="relative">
                <EditorContent editor={editor} />
                {editor && editor.isEmpty && (
                  <div className="absolute top-2 left-3 text-muted-foreground/50 text-sm pointer-events-none select-none">
                    {t("pages.noteEditor.addNotePlaceholder", "Add a note...")}
                  </div>
                )}
              </div>
              {editor && (
                <div className="flex items-center justify-between px-2 py-1.5 border-t border-border/30">
                  <div className="flex items-center gap-0.5 text-muted-foreground">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={editor.isActive("bold") ? "h-6 w-6 p-0 text-primary bg-primary/10" : "h-6 w-6 p-0 hover:text-foreground"}
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      disabled={!editor.can().chain().focus().toggleBold().run()}
                      title={t("pages.noteEditor.bold", "Bold")}
                    >
                      <Bold className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={editor.isActive("italic") ? "h-6 w-6 p-0 text-primary bg-primary/10" : "h-6 w-6 p-0 hover:text-foreground"}
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      disabled={!editor.can().chain().focus().toggleItalic().run()}
                      title={t("pages.noteEditor.italic", "Italic")}
                    >
                      <Italic className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={editor.isActive("underline") ? "h-6 w-6 p-0 text-primary bg-primary/10" : "h-6 w-6 p-0 hover:text-foreground"}
                      onClick={() => editor.chain().focus().toggleUnderline().run()}
                      disabled={!editor.can().chain().focus().toggleUnderline().run()}
                      title={t("pages.noteEditor.underline", "Underline")}
                    >
                      <UnderlineIcon className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={editor.isActive("strike") ? "h-6 w-6 p-0 text-primary bg-primary/10" : "h-6 w-6 p-0 hover:text-foreground"}
                      onClick={() => editor.chain().focus().toggleStrike().run()}
                      disabled={!editor.can().chain().focus().toggleStrike().run()}
                      title={t("pages.noteEditor.strikethrough", "Strikethrough")}
                    >
                      <Strikethrough className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={editor.isActive("code") ? "h-6 w-6 p-0 text-primary bg-primary/10" : "h-6 w-6 p-0 hover:text-foreground"}
                      onClick={() => editor.chain().focus().toggleCode().run()}
                      disabled={!editor.can().chain().focus().toggleCode().run()}
                      title={t("pages.noteEditor.inlineCode", "Inline Code")}
                    >
                      <Code className="w-3.5 h-3.5" />
                    </Button>
                    <div className="w-px h-3 bg-border/50 mx-0.5" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={editor.isActive("bulletList") ? "h-6 w-6 p-0 text-primary bg-primary/10" : "h-6 w-6 p-0 hover:text-foreground"}
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      disabled={!editor.can().chain().focus().toggleBulletList().run()}
                      title={t("pages.noteEditor.bulletList", "Bullet List")}
                    >
                      <List className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={editor.isActive("orderedList") ? "h-6 w-6 p-0 text-primary bg-primary/10" : "h-6 w-6 p-0 hover:text-foreground"}
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      disabled={!editor.can().chain().focus().toggleOrderedList().run()}
                      title={t("pages.noteEditor.numberedList", "Numbered List")}
                    >
                      <ListOrdered className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
);

SessionNoteInput.displayName = "SessionNoteInput";
